// Tata letak struk pembayaran -- SATU aturan untuk pratinjau, cetak browser, dan printer Bluetooth web.
// Aturan ini sama dengan struk kasir di aplikasi Flutter (lib/struk/struk_layout.dart); keduanya dijaga sama oleh
// tes berbasis berkas contoh (fixtures/*.json). Mengubah susunan struk = ubah di kedua tempat + berkas contoh.
//
// Keluaran: daftar baris { teks, rata: 'kiri'|'tengah'|'kanan', tebal: bool }, { gambar: url, rata }, atau { qr: tautan, rata }.

export const TEKS_ULASAN = 'Scan untuk beri ulasan di Google';

export const KOLOM_58MM = 32;
export const KOLOM_80MM = 48;

// Bagian struk yang bisa disembunyikan admin cabang (kunci sama dengan backend utils/strukPengaturan.js). Tidak ada = tampil.
export const BAGIAN_STRUK = ['logo', 'telepon', 'wifi', 'antrian', 'pelanggan', 'kasir', 'waktu', 'orderId', 'meja', 'catatan', 'ulasan'];

/**
 * Opsi cetak dari konfigurasi cabang (kolom strukLebar / strukTampil / strukCatatanKaki).
 * @returns {{kolom:number, tampil:object, catatanKaki:string}}
 */
export const opsiDariKonfigurasi = (k) => ({
  kolom: Number(k && k.strukLebar) === 80 ? KOLOM_80MM : KOLOM_58MM,
  tampil: (k && k.strukTampil && typeof k.strukTampil === 'object') ? k.strukTampil : {},
  catatanKaki: String((k && k.strukCatatanKaki) || '').trim(),
  urutan: (k && k.strukUrutan && typeof k.strukUrutan === 'object') ? k.strukUrutan : null,
});

// Urutan bawaan struk: urutan blok, dan urutan baris di dalam blok. Admin cabang bisa mengubahnya (menu Struk).
// Pembatas (garis = / -) ikut blok & tidak diurutkan sendiri; dua pembatas yang bertemu digabung.
export const URUTAN_BAWAAN = {
  blok: ['kepala', 'info', 'item', 'ringkasan', 'kaki'],
  kepala: ['logo', 'nama', 'telepon', 'wifi', 'teksA', 'teksB'],
  info: ['antrian', 'pembayaran', 'pelanggan', 'kasir', 'waktu', 'orderId', 'meja'],
  ringkasan: ['promo', 'ppn', 'total', 'bayar'],
};

/** Melengkapi urutan dari pengaturan: kunci tak dikenal / ganda dibuang, yang kurang ditambahkan di akhir menurut urutan bawaan. */
export const urutanLengkap = (u) => {
  const hasil = {};
  for (const kunci of Object.keys(URUTAN_BAWAAN)) {
    const dasar = URUTAN_BAWAAN[kunci];
    const pilih = (u && Array.isArray(u[kunci]) ? u[kunci] : []).filter((x, i, a) => dasar.includes(x) && a.indexOf(x) === i);
    hasil[kunci] = [...pilih, ...dasar.filter((x) => !pilih.includes(x))];
  }
  return hasil;
};

export const rupiah = (angka) => {
  const n = Math.round(Number(angka) || 0);
  const digit = Math.abs(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${n < 0 ? '-' : ''}Rp ${digit}`;
};

const dua = (n) => String(n).padStart(2, '0');

/** dd/MM/yyyy HH:mm dari Date yang sudah digeser ke jam dinding cabang (lihat utils/waktuCabang). */
export const formatWaktuStruk = (d) =>
  `${dua(d.getDate())}/${dua(d.getMonth() + 1)}/${d.getFullYear()} ${dua(d.getHours())}:${dua(d.getMinutes())}`;

const ada = (v) => v !== undefined && v !== null && String(v).length > 0;

/** Membungkus teks bebas (catatan kaki) per kata selebar kolom; kata lebih panjang dari kolom dipotong. */
const bungkusKata = (t, kolom) => {
  const hasil = [];
  let baris = '';
  for (const kata of String(t).split(/\s+/).filter(Boolean)) {
    let sisa = kata;
    while (sisa.length > kolom) {
      if (baris) { hasil.push(baris); baris = ''; }
      hasil.push(sisa.slice(0, kolom));
      sisa = sisa.slice(kolom);
    }
    if (!baris) baris = sisa;
    else if (baris.length + 1 + sisa.length <= kolom) baris += ` ${sisa}`;
    else { hasil.push(baris); baris = sisa; }
  }
  if (baris) hasil.push(baris);
  return hasil;
};

/**
 * @param {object} p
 * @param {object|null} p.cabang  { nama, noTelp, wifiNama, wifi, teksA, teksB, logoUrl, ulasanUrl } -- null = tanpa konfigurasi
 * @param {object} p.trx          lihat bidang di bawah
 * @param {number} [p.kolom=32]       32 = kertas 58 mm, 48 = 80 mm
 * @param {object} [p.tampil]         sakelar bagian struk ({ logo:false, ... }); tidak ada = tampil
 * @param {string} [p.catatanKaki]    mengganti penutup "Terima Kasih!" bila diisi (maks. 3 baris)
 * @param {object} [p.urutan]         { blok, kepala, info, ringkasan }: urutan blok & baris (lihat URUTAN_BAWAAN); kosong = bawaan
 * Hasil memuat properti `kolom` supaya pencetak/pratinjau memakai lebar yang sama.
 */
export const buatBarisStruk = ({ cabang, trx, kolom = KOLOM_58MM, tampil = {}, catatanKaki = '', urutan = null }) => {
  const on = (bagian) => tampil[bagian] !== false;
  const bar = (t, rata = 'kiri', tebal = false) => ({ teks: String(t), rata, tebal });
  const garis = (c) => ({ teks: c.repeat(kolom), rata: 'tengah', tebal: false, garis: true });
  const urut = urutanLengkap(urutan);

  // --- baris tiap bagian ---
  const kepala = {
    logo: () => (cabang && on('logo') && ada(cabang.logoUrl) ? [{ gambar: cabang.logoUrl, rata: 'tengah' }] : []),
    nama: () => [bar(String((cabang ? cabang.nama : trx.namaCabang) || '').toUpperCase(), 'tengah', true)],
    telepon: () => (cabang && on('telepon') && ada(cabang.noTelp) ? [bar(`Telp: ${cabang.noTelp}`, 'tengah')] : []),
    wifi: () => (cabang && on('wifi') && ada(cabang.wifi)
      ? [bar(ada(cabang.wifiNama) ? `WiFi: ${cabang.wifiNama} / ${cabang.wifi}` : `WiFi: ${cabang.wifi}`, 'tengah')] : []),
    teksA: () => (cabang && ada(cabang.teksA) ? [bar(cabang.teksA, 'tengah')] : []),
    teksB: () => (cabang && ada(cabang.teksB) ? [bar(cabang.teksB, 'tengah')] : []),
  };
  const info = {
    antrian: () => (on('antrian') && ada(trx.nomorAntrian) ? [bar(`ANTRIAN ${trx.nomorAntrian}`, 'tengah', true)] : []),
    pembayaran: () => (ada(trx.metodeBayar) ? [bar(`Pembayaran: ${trx.metodeBayar}`)] : []),
    pelanggan: () => (on('pelanggan') ? [bar(`Customer: ${trx.customer}`)] : []),
    kasir: () => (on('kasir') ? [bar(`Cashier: ${trx.kasir}`)] : []),
    waktu: () => (on('waktu') ? [bar(trx.waktu)] : []),
    orderId: () => (on('orderId') ? [bar(`Order ID: ${trx.orderId}`)] : []),
    meja: () => (on('meja') && ada(String(trx.meja || '').trim()) ? [bar(`Meja: ${String(trx.meja).trim()}`)] : []),
  };

  const diskon = Number(trx.diskonPromo) || 0;
  const ppn = Number(trx.ppnNominal) || 0;
  const total = Number(trx.total) || 0;
  const ringkasan = {
    promo: () => {
      if (diskon <= 0) return [];
      const subtotalNetto = trx.subtotal ?? total - ppn;
      return [
        bar(`Sebelum promo: ${rupiah(subtotalNetto + diskon)}`, 'kanan'),
        bar(`Promo${ada(trx.namaPromo) ? ` ${trx.namaPromo}` : ''}: -${rupiah(diskon)}`, 'kanan'),
      ];
    },
    ppn: () => (ppn > 0
      ? [bar(`Subtotal: ${rupiah(trx.subtotal ?? total - ppn)}`, 'kanan'), bar(`PPN ${Number(trx.ppnPersen) || 0}%: ${rupiah(ppn)}`, 'kanan')] : []),
    total: () => [bar(`TOTAL: ${rupiah(total)}`, 'kanan', true)],
    bayar: () => {
      if (trx.barisBagi && trx.barisBagi.length > 0) return trx.barisBagi.map((b) => bar(b, 'kanan'));
      if (String(trx.metodeBayar || '').toLowerCase() === 'cash') {
        const uang = Number(trx.uangCustomer) || 0;
        return [bar(`UANG CUSTOMER: ${rupiah(uang)}`, 'kanan'), bar(`KEMBALIAN: ${rupiah(uang - total)}`, 'kanan')];
      }
      return [];
    },
  };

  // --- blok (pembatas ikut blok) ---
  const blok = {
    kepala: () => [...urut.kepala.flatMap((k) => kepala[k]()), garis('=')],
    info: () => urut.info.flatMap((k) => info[k]()),
    item: () => [
      garis('-'),
      bar('Items'),
      garis('-'),
      ...trx.items.flatMap((it) => [
        bar(it.nama),
        bar(`${rupiah(it.harga)} x ${it.qty} = ${rupiah(it.harga * it.qty)}`, 'kanan'),
        ...(on('catatan') && ada(it.catatan) ? [bar(`  * ${it.catatan}`)] : []),
      ]),
      ...(on('catatan') ? [bar(`Noted: ${trx.noted || ''}`)] : []),
      garis('-'),
    ],
    ringkasan: () => [...urut.ringkasan.flatMap((k) => ringkasan[k]()), garis('=')],
    kaki: () => {
      const hasil = [bar(' ', 'tengah', true)];
      const kaki = String(catatanKaki || '').trim();
      if (kaki) {
        for (const b of kaki.split('\n')) for (const k of bungkusKata(b, kolom)) hasil.push(bar(k, 'tengah'));
      } else {
        const sisa = Math.max(0, kolom - 'Terima Kasih!'.length);
        const kiri = Math.floor(sisa / 2);
        hasil.push(bar(`${'-'.repeat(kiri)}Terima Kasih!${'-'.repeat(sisa - kiri)}`, 'tengah'));
      }
      // QR tautan ulasan Google cabang (sakelar "ulasan"; hanya bila tautannya diisi di menu Struk).
      if (cabang && on('ulasan') && ada(cabang.ulasanUrl)) {
        hasil.push(bar(' ', 'tengah'), { qr: String(cabang.ulasanUrl), rata: 'tengah' });
        for (const k of bungkusKata(TEKS_ULASAN, kolom)) hasil.push(bar(k, 'tengah'));
      }
      return hasil;
    },
  };

  // Pembatas di awal struk dibuang; dua pembatas bertemu (mis. karena blok kosong / dipindah) digabung jadi satu.
  const baris = [];
  baris.kolom = kolom;
  for (const b of urut.blok.flatMap((k) => blok[k]())) {
    if (b.garis && (baris.length === 0 || baris[baris.length - 1].garis)) continue;
    baris.push(b);
  }
  return baris;
};

// ---------------------------------------------------------------------------------------------
// Tiket dapur & bar (dicetak ke printer KITCHEN / BAR, tanpa harga). Sama dengan buatBarisTiket di Flutter
// (lib/struk/struk_layout.dart). Pengaturan per cabang: kolom konfigurasis.tiketDapur / tiketBar.

export const JUDUL_TIKET = { dapur: '*** KITCHEN ORDER ***', bar: '*** BAR ORDER ***' };
export const PENUTUP_TIKET = 'Tolong Persiapkan Orderan Ini';

/** Bagian tiket yang bisa disembunyikan (kunci sama dengan backend TIKET_TAMPIL). Tidak ada = tampil. */
export const BAGIAN_TIKET = ['judul', 'nama', 'antrian', 'meja', 'pelanggan', 'kasir', 'waktu', 'orderId', 'catatanItem', 'catatan', 'penutup'];

export const URUTAN_TIKET_BAWAAN = {
  blok: ['kepala', 'info', 'item', 'catatan', 'kaki'],
  kepala: ['nama', 'judul'],
  info: ['antrian', 'orderId', 'pelanggan', 'meja', 'waktu', 'kasir'],
};

export const urutanTiketLengkap = (u) => {
  const hasil = {};
  for (const kunci of Object.keys(URUTAN_TIKET_BAWAAN)) {
    const dasar = URUTAN_TIKET_BAWAAN[kunci];
    const pilih = (u && Array.isArray(u[kunci]) ? u[kunci] : []).filter((x, i, a) => dasar.includes(x) && a.indexOf(x) === i);
    hasil[kunci] = [...pilih, ...dasar.filter((x) => !pilih.includes(x))];
  }
  return hasil;
};

/** Opsi tiket dari konfigurasi cabang. @param {'dapur'|'bar'} jenis */
export const opsiTiketDariKonfigurasi = (k, jenis) => {
  const t = (k && (jenis === 'bar' ? k.tiketBar : k.tiketDapur)) || {};
  return {
    jenis,
    kolom: Number(t.lebar) === 80 ? KOLOM_80MM : KOLOM_58MM,
    judul: String(t.judul || '').trim(),
    gayaItem: t.gayaItem === 'x' ? 'x' : 'qty',
    penutup: String(t.penutup || '').trim(),
    tampil: t.tampil && typeof t.tampil === 'object' ? t.tampil : {},
    urutan: t.urutan && typeof t.urutan === 'object' ? t.urutan : null,
  };
};

/**
 * @param {object} p
 * @param {object} p.trx  { namaCabang, nomorAntrian, meja, customer, kasir, waktu, orderId, noted, items:[{ nama, qty, catatan }] }
 * @param {'dapur'|'bar'} [p.jenis]
 * @param {number} [p.kolom] 32 = 58 mm, 48 = 80 mm
 * @param {string} [p.judul]     kosong = bawaan ("*** KITCHEN ORDER ***" / "*** BAR ORDER ***")
 * @param {'qty'|'x'} [p.gayaItem]  'qty' = nama lalu "Qty: 2"; 'x' = "2x nama" (tebal)
 * @param {string} [p.penutup]  kosong = bawaan ("Tolong Persiapkan Orderan Ini")
 */
export const buatBarisTiket = ({ trx, jenis = 'dapur', kolom = KOLOM_58MM, judul = '', gayaItem = 'qty', penutup = '', tampil = {}, urutan = null }) => {
  const on = (bagian) => tampil[bagian] !== false;
  const bar = (t, rata = 'kiri', tebal = false) => ({ teks: String(t), rata, tebal });
  const garis = (c) => ({ teks: c.repeat(kolom), rata: 'tengah', tebal: false, garis: true });
  const urut = urutanTiketLengkap(urutan);

  const kepala = {
    nama: () => (on('nama') && ada(trx.namaCabang) ? [bar(String(trx.namaCabang).toUpperCase(), 'tengah', true)] : []),
    judul: () => (on('judul') ? [bar(ada(judul) ? judul : JUDUL_TIKET[jenis] || JUDUL_TIKET.dapur, 'tengah', true)] : []),
  };
  const info = {
    antrian: () => (on('antrian') && ada(trx.nomorAntrian) ? [bar(`ANTRIAN ${trx.nomorAntrian}`, 'tengah', true)] : []),
    orderId: () => (on('orderId') && ada(trx.orderId) ? [bar(`Order ID: ${trx.orderId}`)] : []),
    pelanggan: () => (on('pelanggan') && ada(trx.customer) ? [bar(`Customer: ${trx.customer}`)] : []),
    meja: () => (on('meja') && ada(String(trx.meja || '').trim()) ? [bar(`Meja: ${String(trx.meja).trim()}`)] : []),
    waktu: () => (on('waktu') && ada(trx.waktu) ? [bar(trx.waktu)] : []),
    kasir: () => (on('kasir') && ada(trx.kasir) ? [bar(`Kasir: ${trx.kasir}`)] : []),
  };

  const blok = {
    kepala: () => [...urut.kepala.flatMap((k) => kepala[k]()), garis('=')],
    info: () => urut.info.flatMap((k) => info[k]()),
    item: () => [
      garis('-'),
      bar('Orderan:'),
      garis('-'),
      ...trx.items.flatMap((it) => [
        ...(gayaItem === 'x' ? [bar(`${it.qty}x ${it.nama}`, 'kiri', true)] : [bar(it.nama), bar(`Qty: ${it.qty}`)]),
        ...(on('catatanItem') && ada(it.catatan) ? [bar(`  * ${it.catatan}`)] : []),
      ]),
      garis('-'),
    ],
    catatan: () => (on('catatan') && ada(String(trx.noted || '').trim()) ? [bar('NOTES:'), bar(String(trx.noted).trim()), garis('-')] : []),
    kaki: () => {
      const hasil = [garis('=')];
      if (on('penutup')) {
        const teksPenutup = ada(penutup) ? penutup : PENUTUP_TIKET;
        for (const b of teksPenutup.split('\n')) for (const k of bungkusKata(b, kolom)) hasil.push(bar(k, 'tengah'));
      }
      return hasil;
    },
  };

  // Pembatas di awal dibuang; dua pembatas bertemu digabung dan yang TERAKHIR dipakai (garis = sebelum penutup, seperti tiket lama).
  const baris = [];
  baris.kolom = kolom;
  for (const b of urut.blok.flatMap((k) => blok[k]())) {
    if (b.garis && baris.length === 0) continue;
    if (b.garis && baris[baris.length - 1].garis) { baris[baris.length - 1] = b; continue; }
    baris.push(b);
  }
  return baris;
};

// ---------------------------------------------------------------------------------------------
// Struk tutup shift (laporan kas kasir). Sama dengan cetakStrukShift di Flutter (lib/shift/shift_ui.dart).

const BULAN = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];

/** "d MMM, HH:mm" (mis. "17 Okt, 14:05") dari Date yang sudah digeser ke jam dinding. */
export const formatWaktuShift = (d) => `${d.getDate()} ${BULAN[d.getMonth()]}, ${dua(d.getHours())}:${dua(d.getMinutes())}`;

/**
 * @param {object} p
 * @param {string} p.kasir
 * @param {string} p.buka  waktu buka (sudah diformat)
 * @param {string} p.tutup waktu tutup (sudah diformat)
 * @param {object} p.h     { kasAwal, kasSeharusnya, kasDihitung, selisih, catatan, ringkasan:{ metode:{cash,qris,qris_statis,noncash:{jumlah,total}}, kasMasuk, kasKeluar, penjualan, jumlahTransaksi, batal:{jumlah,total} } }
 */
export const buatBarisShift = ({ kasir, buka, tutup, h, kolom = KOLOM_58MM }) => {
  const baris = [];
  baris.kolom = kolom;
  const teks = (t, rata = 'kiri', tebal = false) => baris.push({ teks: String(t), rata, tebal });
  const rp = (v) => rupiah(v).replace('Rp ', '');
  const kiriKanan = (a, b) => {
    const spasi = kolom - a.length - b.length;
    teks(spasi > 0 ? `${a}${' '.repeat(spasi)}${b}` : `${a} ${b}`);
  };
  const r = h.ringkasan || {};
  const metode = r.metode || {};

  teks('LAPORAN TUTUP SHIFT', 'tengah', true);
  teks(`Kasir: ${kasir}`);
  teks(`Buka : ${buka}`);
  teks(`Tutup: ${tutup}`);
  teks('-'.repeat(kolom));
  kiriKanan('Kas awal', rp(h.kasAwal));
  kiriKanan(`Tunai (${(metode.cash && metode.cash.jumlah) || 0})`, rp(metode.cash && metode.cash.total));
  // Pelunasan piutang (uang muka sudah ikut Tunai di atas): hanya tampil bila ada, supaya shift biasa tidak berubah.
  const lunas = r.pelunasan || {};
  if ((Number(lunas.tunai) || 0) > 0) kiriKanan('Lunas piutang', rp(lunas.tunai));
  if ((Number(lunas.dikembalikanTunai) || 0) > 0) kiriKanan('Kembali piutang', `-${rp(lunas.dikembalikanTunai)}`);
  kiriKanan('Kas masuk', rp(r.kasMasuk));
  kiriKanan('Kas keluar', `-${rp(r.kasKeluar)}`);
  kiriKanan('Seharusnya', rp(h.kasSeharusnya));
  kiriKanan('Dihitung', rp(h.kasDihitung));
  const selisih = Number(h.selisih) || 0;
  kiriKanan('SELISIH', `${selisih < 0 ? '-' : selisih > 0 ? '+' : ''}${rp(Math.abs(selisih))}`);
  teks('-'.repeat(kolom));
  for (const [kunci, nama] of [['qris', 'QRIS'], ['qris_statis', 'QRIS statis'], ['noncash', 'Non-tunai']]) {
    const m = metode[kunci] || {};
    if ((Number(m.jumlah) || 0) > 0) kiriKanan(`${nama} (${m.jumlah})`, rp(m.total));
  }
  if ((Number(lunas.nonTunai) || 0) > 0) kiriKanan('Lunas piutang non-tunai', rp(lunas.nonTunai));
  kiriKanan(`Total jual (${r.jumlahTransaksi || 0})`, rp(r.penjualan));
  const piutangBaru = r.piutangBaru || {};
  if ((Number(piutangBaru.jumlah) || 0) > 0) kiriKanan(`Piutang baru (${piutangBaru.jumlah})`, rp(piutangBaru.total));
  const batal = r.batal || {};
  if ((Number(batal.jumlah) || 0) > 0) kiriKanan(`Batal (${batal.jumlah})`, rp(batal.total));
  if (ada(h.catatan)) teks(`Catatan: ${h.catatan}`);
  teks(' ');
  teks(' ');
  return baris;
};

// ---------------------------------------------------------------------------------------------
// Penyajian teks monospace (pratinjau & berkas contoh). Printer thermal membungkus per karakter
// pada lebar kolomnya, jadi pembungkusan di sini juga per karakter.

const bungkus = (t, kolom) => {
  if (t.length <= kolom) return [t];
  const hasil = [];
  for (let i = 0; i < t.length; i += kolom) hasil.push(t.slice(i, i + kolom));
  return hasil;
};

const ratakan = (t, rata, kolom) => {
  if (rata === 'kanan') return t.padStart(kolom, ' ');
  if (rata === 'tengah') return ' '.repeat(Math.floor((kolom - t.length) / 2)) + t;
  return t;
};

/** Baris -> [{ teks, tebal }] siap tampil (sudah dibungkus & diratakan); gambar jadi penanda '[LOGO]', QR jadi '[QR ULASAN]'. */
export const sajikanBaris = (baris, kolom = KOLOM_58MM) => {
  const hasil = [];
  for (const b of baris) {
    if (b.gambar) {
      hasil.push({ teks: ratakan('[LOGO]', 'tengah', kolom), tebal: false, gambar: b.gambar });
      continue;
    }
    if (b.qr) {
      hasil.push({ teks: ratakan('[QR ULASAN]', 'tengah', kolom), tebal: false, qr: b.qr });
      continue;
    }
    for (const potong of bungkus(b.teks, kolom)) {
      hasil.push({ teks: ratakan(potong, b.rata, kolom).replace(/\s+$/, ''), tebal: !!b.tebal });
    }
  }
  return hasil;
};

/** Hanya teksnya (untuk perbandingan dengan berkas contoh). */
export const teksStruk = (baris, kolom = KOLOM_58MM) => sajikanBaris(baris, kolom).map((b) => b.teks);
